import { SYMPHONY_WHATSAPP_INBOUND_NUMBER } from '@wix/ai-scheduling-assistant-constants';

// Display-only decoding of WhatsApp GROUP messages. In a group the server bakes
// a sender attribution prefix into each inbound member turn's stored content —
// `[<displayName> (<waId>)( OWNER)?]: <text>` (digest-engine's
// `formatAttributedGroupMessage`) — so the LLM can tell members apart. The web
// stores nothing extra, so this module reconstructs the sender from that prefix
// for a group-chat presentation. Nothing here changes how messages are stored.

export interface GroupAttribution {
  /** The sender's display name as attributed by the server. */
  displayName: string;
  /** The sender's WhatsApp id (bare digits). */
  waId: string;
  /** True when the server tagged this sender as the group's OWNER (the account holder). */
  isOwner: boolean;
  /** The message body with the attribution prefix stripped. */
  text: string;
}

// Deterministic, mirrors the server format exactly. Non-greedy name so a name
// that itself contains parens still binds the waId to the trailing `(digits)`.
const ATTRIBUTION_RE = /^\[(.+?) \((\d+)\)( OWNER)?\]:\s?([\s\S]*)$/;

/**
 * Parse the server's group attribution prefix off a message's content. Returns
 * the sender + stripped text, or null when the content isn't an attributed
 * group line (every 1:1 message, and the assistant's own group replies).
 */
export function parseGroupAttribution(content: string | undefined | null): GroupAttribution | null {
  if (!content) return null;
  const match = ATTRIBUTION_RE.exec(content);
  if (!match) return null;
  return { displayName: match[1], waId: match[2], isOwner: Boolean(match[3]), text: match[4] ?? '' };
}

/** Initials for an avatar: first + last word initial (or the first two letters of a single word). */
export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Build a `waId → displayName` roster by parsing every attributed line in a
 * conversation, so `@<number>` mentions can be resolved to a readable name.
 */
export function buildParticipantRoster(contents: Array<string | undefined | null>): Map<string, string> {
  const roster = new Map<string, string>();
  for (const content of contents) {
    const attribution = parseGroupAttribution(content);
    if (attribution) roster.set(attribution.waId, attribution.displayName);
  }
  return roster;
}

export interface MentionResolver {
  /** The assistant's own name to show for a mention of its own WhatsApp number. */
  assistantName: string;
  /** The assistant's WhatsApp number. Defaults to the shared constant. */
  assistantWaId?: string;
  /** Known group participants, `waId → displayName`. */
  participantsByWaId?: Map<string, string>;
}

// WhatsApp waIds are long (country code + number); require 5+ digits so ordinary
// numbers in prose ("@3 items") aren't rewritten.
const MENTION_RE = /@(\d{5,})/g;

/**
 * Resolve `@<number>` mentions to readable names: the assistant's own number →
 * the assistant name; a known participant's waId → their display name; anything
 * else is left exactly as written.
 */
export function resolveGroupMentions(text: string, resolver: MentionResolver): string {
  const assistantWaId = resolver.assistantWaId ?? SYMPHONY_WHATSAPP_INBOUND_NUMBER;
  return text.replace(MENTION_RE, (whole, digits: string) => {
    if (digits === assistantWaId) return `@${resolver.assistantName}`;
    const name = resolver.participantsByWaId?.get(digits);
    return name ? `@${name}` : whole;
  });
}
